import { html, render } from "lit-html";
import { Input } from "./input";
import Palette from "../palette";

const Slots = {
  NONE: 0,
  WEAPON: 1,
  ARMOR: 2,
  QUIVER: 3,
  INVENTORY: 4
}

const ListText = new Intl.ListFormat("en", {style: "long", type: "conjunction" });

class EquipDialog {
  constructor() {
    this.overlay = document.querySelector(".overlay");
    this.dialog = document.querySelector(".dialog");
    this.selectedSlot = Slots.NONE;
    this.selectedItem = -1;
    this.isDirty = false;
  }

  handleInput(key, stage) {
    switch (key) {
      case " ":
      case "q":
        document.dispatchEvent(new CustomEvent("ui.transition", {
          detail: {
            command: "CloseDialog",
          }
        }));
      break;

      case "w":
        this.selectSlot(Slots.WEAPON);
      break;

      case "a":
        this.selectSlot(Slots.ARMOR);
      break;

      case "x":
        this.selectSlot(Slots.QUIVER);
      break;

      case "i":
        this.selectSlot(Slots.INVENTORY);
      break;

      case "g":
        this.markDirty();
        return {
          ...Input.Get,
          detail: stage.entitiesAt(stage.player.x, stage.player.y).filter(entity => entity.isItem())
        }
      break;

      case "d":
        this.markDirty();
        return {
          ...Input.Drop,
          detail: {
            slot: this.selectedSlot,
            index: this.selectedItem
          }
        }
      break;

      case "ArrowRight":
        if (this.selectedSlot == Slots.NONE) {
          this.selectSlot(Slots.WEAPON);
        } else if (this.selectedSlot == Slots.INVENTORY) {
          this.selectSlot(Slots.WEAPON);
        } else {
          this.selectSlot(this.selectedSlot + 1);
        }
      break;

      case "ArrowLeft":
        if (this.selectedSlot == Slots.NONE) {
          this.selectSlot(Slots.INVENTORY);
        } else if (this.selectedSlot == Slots.WEAPON) {
          this.selectSlot(Slots.INVENTORY);
        } else {
          this.selectSlot(this.selectedSlot - 1);
        }
      break;

      case "ArrowUp":
        if (this.selectedSlot == Slots.INVENTORY) {
          const inventory = stage.player.equipment.inventory;
          if (inventory.length > 0) {
            const increment = (this.selectedItem - 1) % inventory.length;
            this.selectItem(increment < 0 ? inventory.length - 1 : increment);
          }
        } else {
          if (this.selectedSlot == Slots.WEAPON) {
            this.selectSlot(Slots.INVENTORY);
          } else {
            this.selectSlot(this.selectedSlot - 1);
          }
        }
      break;

      case "ArrowDown":
        if (this.selectedSlot == Slots.INVENTORY) {
          const inventory = stage.player.equipment.inventory;
          if (inventory.length > 0) {
            this.selectItem((this.selectedItem + 1) % inventory.length);
          }
        } else {
          this.selectSlot(this.selectedSlot + 1);
        }
      break;

      case "Enter":
        if (this.selectedSlot == Slots.INVENTORY) {
          stage.player.equipment.equipFromInventory(this.selectedItem);
          if (this.selectedItem >= stage.player.equipment.length) {
            this.selectedItem = stage.player.equipment.length - 1;
          }
          this.markDirty();
        } else if (this.selectedSlot == Slots.WEAPON) {
          stage.player.equipment.unequipWeapon();
          this.markDirty();
        } else if (this.selectedSlot == Slots.ARMOR) {
          stage.player.equipment.unequipArmor();
          this.markDirty();
        } else if (this.selectedSlot == Slots.QUIVER) {
          stage.player.equipment.unequipQuiver();
          this.markDirty();
        }
      break;

      default:
        if (isFinite(key)) {
          const inventory = stage.player.equipment.inventory;
          const index = (key == "0") ? 9 : Number(key) - 1;
          if (index >= inventory.length - 1) {
            this.selectItem(inventory.length - 1);
          } else {
            this.selectItem(index);
          }
          this.selectSlot(Slots.INVENTORY);
        }

      break;
    }
    return false;
  }

  markDirty() {
    this.isDirty = true;
  }

  selectSlot(slot) {
    this.selectedSlot = slot;
    this.isDirty = true;
  }

  selectItem(index) {
    this.selectedItem = index;
    this.isDirty = true;
  }

  pickupItem() {

  }

  rescale() {
    return;
  }

  activate(context) {
    this.overlay.classList.add("is-visible");
    this.dialog.classList.add("is-visible");
    this.dialog.classList.add("is-sheet");
    this.isDirty = true;
  }

  deactivate(context) {
    this.overlay.classList.remove("is-visible");
    this.dialog.classList.remove("is-visible");
    this.dialog.classList.remove("is-sheet");
    this.isDirty = false;
  }

  getEquippedWeapon(weapon, selected) {
    const listClass = selected == Slots.WEAPON ? "sheet-list is-selected" : "sheet-list";
    const itemClass = weapon ? "sheet-list-item item" : "sheet-list-item item is-empty";
    const itemGlyph = weapon ? weapon.glyph.character : "-";
    const itemColor = weapon ? weapon.glyph.foregroundColor : Palette.Ashen;
    const itemName = weapon ? weapon.name : "bare hands";
    const glyphStyle = `color: ${itemColor}`;
    const weaponDamage = weapon && weapon.weapon && weapon.weapon.type == "melee" ? weapon.weapon.damage : "1";
    const equipTitle = weapon ? weapon.isWeapon() ? "Weapon" : "Item" : "Weapon";

    return html`
      <div class=${listClass}>
        <p class="sheet-list-name">${equipTitle}</p>
        <div class=${itemClass}>
          <div class="item-shortcut">[w]</div>
          <div class="item-glyph" style=${glyphStyle}>${itemGlyph}</div>
          <div class="item-name">${itemName}</div>
          ${this.getWeaponStats(weapon)}
        </div>
      </div>
    `;
  }

  getEquippedArmor(armor, selected) {
    const listClass = selected == Slots.ARMOR ? "sheet-list is-selected" : "sheet-list";
    const itemClass = armor ? "sheet-list-item item" : "sheet-list-item item is-empty";
    const itemGlyph = armor ? armor.glyph.character : "-";
    const itemColor = armor ? armor.glyph.foregroundColor : Palette.Ashen;
    const itemName = armor ? armor.name : "unprotected";
    const glyphStyle = `color: ${itemColor}`;
    const armorBonus = armor ? armor.armor.bonus : "0";

    return html`
      <div class=${listClass}>
        <p class="sheet-list-name">Armor</p>
        <div class=${itemClass}>
          <div class="item-shortcut">[a]</div>
          <div class="item-glyph" style=${glyphStyle}>${itemGlyph}</div>
          <div class="item-name">${itemName}</div>
          ${this.getArmorStats(armor)}
        </div>
      </div>
    `;
  }

  getInventoryItem(index, item) {
    const itemClass = this.selectedSlot == Slots.INVENTORY && index == this.selectedItem ? "sheet-list-item item is-selected" : "sheet-list-item item";
    const maybeEmptyItemClass = item ? itemClass : `${itemClass} is-empty`;
    const itemGlyph = item ? item.glyph.character : "-";
    const itemColor = item ? item.glyph.foregroundColor : Palette.Ashen;
    const itemName = item ? item.name : "empty";
    const glyphStyle = `color: ${itemColor}`;

    const statBlock = item ? item.isWeapon() ? this.getWeaponStats(item) : item.isArmor() ? this.getArmorStats(item) : item.isAmmunition() ? this.getAmmoStats(item) : "" : "";

    return html`
      <div class=${maybeEmptyItemClass}>
        <span class="item-shortcut">[${(index + 1) % 10}]</span>
        <span class="item-glyph" style=${glyphStyle}>${itemGlyph}</span>
        <span class="item-name">${itemName}</span>
        ${statBlock}
      </div>
    `;
  }

  getWeaponStats(weapon) {
    let weaponDamage = "";

    if (weapon && weapon.weapon && weapon.weapon.type == "melee") {
      weaponDamage = `▵${weapon.weapon.damage}`;
    }

    return html`
      <div class="item-hit">${weaponDamage}</div>
    `;
  }

  getArmorStats(armor) {
    const armorBonus = armor ? armor.armor.bonus : "0";

    return html`
      <div class="item-bonus">•${armorBonus}</div>
    `;
  }

  getAmmoStats(ammo) {
    const ammoStack = ammo ? `${ammo.ammunition.quantity}/${ammo.ammunition.limit}` : "";
    const ammoDamage = ammo ? `▵${ammo.ammunition.damage}` : "";

    return html`
      <div class="item-stack">${ammoStack}</div>
      <div class="item-hit">${ammoDamage}</div>
    `;
  }

  getEquippedQuiver(ammo, selected) {
    const listClass = selected == Slots.QUIVER ? "sheet-list is-selected" : "sheet-list";
    const itemClass = ammo ? "sheet-list-item item" : "sheet-list-item item is-empty";
    const itemGlyph = ammo ? ammo.glyph.character : "-";
    const itemColor = ammo ? ammo.glyph.foregroundColor : Palette.Ashen;
    const itemName = ammo ? ammo.name : "empty";
    const glyphStyle = `color: ${itemColor}`;
    const ammoStack = ammo ? `${ammo.ammunition.quantity}/${ammo.ammunition.limit}` : "";

    return html`
      <div class=${listClass}>
        <p class="sheet-list-name">Quiver</p>
        <div class=${itemClass}>
          <div class="item-shortcut">[x]</div>
          <div class="item-glyph" style=${glyphStyle}>${itemGlyph}</div>
          <div class="item-name"> ${itemName}</div>
          ${this.getAmmoStats(ammo)}
        </div>
      </div>
    `;
  }

  getUnequippedInventory(inventory, selected) {
    return html`
    <div class=${selected == Slots.INVENTORY ? "sheet-list is-selected" : "sheet-list"}>
        <p class="sheet-list-name">Inventory</p>
        ${this.getInventoryItem(0, inventory[0])}
        ${this.getInventoryItem(1, inventory[1])}
        ${this.getInventoryItem(2, inventory[2])}
        ${this.getInventoryItem(3, inventory[3])}
        ${this.getInventoryItem(4, inventory[4])}
        ${this.getInventoryItem(5, inventory[5])}
        ${this.getInventoryItem(6, inventory[6])}
        ${this.getInventoryItem(7, inventory[7])}
        ${this.getInventoryItem(8, inventory[8])}
        ${this.getInventoryItem(9, inventory[9])}
      </div>
    `;
  }

  getSheetCard(equipment, stage) {
    //let description = stage.map[stage.player.y][stage.player.x].description;
    let description = "";
    const pickups = stage.entitiesAt(stage.player.x, stage.player.y).filter(it => it.isItem());
    if (pickups.length > 0) {
      const pickupsText = pickups.map(it => {
        if (it.isAmmunition()) {
          return `a stack of ${it.name.toLowerCase()}`;
        } else {
          return it.isArmor() ? it.name.toLowerCase() : `a ${it.name.toLowerCase()}`;
        }
      });
      description = `${description} There is ${ListText.format(pickupsText)} on the ground here.`
    }

    if (this.selectedSlot == Slots.WEAPON) {
      if (equipment.weapon) {
        description = equipment.weapon.description;
      }
    } else if (this.selectedSlot == Slots.ARMOR) {
      if (equipment.armor) {
        description = equipment.armor.description;
      }
    } else if (this.selectedSlot == Slots.QUIVER) {
      if (equipment.quiver) {
        description = equipment.quiver.description;
      }
    } else if (this.selectedSlot == Slots.INVENTORY) {
      if (this.selectedItem > -1) {
        if (equipment.inventory[this.selectedItem]) {
          description = equipment.inventory[this.selectedItem].description;
        }
      }
    }

    return html`
      <div class="sheet-card">
        <p class="card-description">${description}</p>
      </div>
    `;
  }

  getDialogTemplate(stage) {
    const equipment = stage.player.equipment;

    return html`
      <h2>Equipment</h2>
      <div class="sheet">
        <div class="sheet-group">
          ${this.getSheetCard(equipment, stage)}
          ${this.getEquippedWeapon(equipment.weapon, this.selectedSlot)}
          ${this.getEquippedArmor(equipment.armor, this.selectedSlot)}
          ${this.getEquippedQuiver(equipment.quiver, this.selectedSlot)}
        </div>
        <div class="sheet-group">
          ${this.getUnequippedInventory(equipment.inventory, this.selectedSlot)}
        </div>
      </div>
    `;
  }

  render(stage) {
    // TODO: experiment with lit-html caching
    if (this.isDirty) {
      render(this.getDialogTemplate(stage), this.dialog);
      this.isDirty = false;
    }
  }
}

export { Slots }
export default EquipDialog;
